import {
    DaperStorySite,
    DaperStoryType,
    type DaperStory,
    type DaperStoryHeadlines,
} from "@bloomberg/intercom";
import type { DaperImages } from "@foundation/intercom-client";

import { CONTENT_TYPE, TRACKING_TYPE } from "~constants";
import type { DaperStoryWithImage } from "~components/modules/user-modules/components/types";
import {
    LOOPING_VIDEO_KEYWORD,
    MOCK_HYDRATED_MEDIA,
    MOCK_HYDRATED_VERTICAL_VIDEO_AS_DECORATION,
    type HydratedMediaSourceData,
} from "~components/shared/media";
import {
    type Audio,
    type CuratedItem,
    CuratedItemVariation,
    type CuratedStory,
    type Eyebrow,
    type Image,
    type LiveBlogPost,
    type MarketMover,
    type Newsletter,
    type RelatedItem,
    type Video,
} from "~types/types";
import type {
    Image as WardenclyffeImage,
    Video as WardenclyffeVideo,
    Metatext,
    Story,
} from "~types/wardenclyffe";

import {
    mockAudio,
    mockHydratedImage,
    mockHydratedVerticalVideo,
    mockHydratedVideo,
} from "./mockMediaBuilder";
import {
    mockHydratedNewsletterCampaign,
    mockHydratedNewsletterImage,
} from "./mockNewsletterBuilder";
import { mockColumnist } from "./mockPersonBuilder";
import {
    type DeepPartial,
    deepMerge,
    deepWrap,
    mockHandler,
} from "./mockUtils";

export const DEFAULT_WARDENCLYFFE_METATEXT: Metatext = {
    html: null,
    seo: null,
    text: null,
    __typename: "Metatext",
};

export const DEFAULT_HYDRATED_EYEBROW: Eyebrow = {
    bybrowEligible: false,
    text: "",
    url: "",
};

export const DEFAULT_MARKET_MOVER: MarketMover = {
    assetId: "",
    percentChange: 0,
    price: 0,
    storyHeadline: "",
    storyUrl: "",
    tickerId: "",
};

export const DEFAULT_WARDENCLYFFE_IMAGE: WardenclyffeImage = {
    alt: null,
    caption: null,
    credit: null,
    chart: null,
    description: null,
    desktop: null,
    height: null,
    id: null,
    isSyndicationPortal: null,
    mobile: null,
    origHeight: null,
    origWidth: null,
    photoCredit: null,
    placement: null,
    privilegeValue: null,
    publishedAt: null,
    size: null,
    source: null,
    submitAppId: null,
    svgFile: null,
    themes: null,
    title: null,
    treatment: null,
    type: null,
    url: null,
    width: null,
    __typename: "Image",
};

export const DEFAULT_WARDENCLYFFE_VIDEO: WardenclyffeVideo = {
    aspectRatio: null,
    autoplay: null,
    avmmIds: null,
    caption: null,
    captions: null,
    cedexisAppID: null,
    compressedIds: null,
    contentLocation: null,
    createdUnixUTC: null,
    credit: null,
    description: null,
    desktop: null,
    duration: null,
    excludedInCountries: null,
    id: null,
    keywords: null,
    mobile: null,
    ni: null,
    prerollAdUnit: null,
    renditions: null,
    renditionType: null,
    showName: null,
    story: null,
    streams: null,
    thumbnail: null,
    title: null,
    transcript: null,
    transcriptLanguage: null,
    type: null,
    __typename: "Video",
};

export const DEFAULT_WARDENCLYFFE_STORY: Story = {
    abstract: null,
    adIds: null,
    adTargeting: null,
    archived: null,
    assistance: null,
    attributor: null,
    audios: null,
    authoredRegion: null,
    authorSession: null,
    autoGeneratedSummary: null,
    autoSummaries: null,
    body: null,
    brand: null,
    byline: null,
    campaign: null,
    categories: null,
    charts: null,
    checksum: null,
    collections: null,
    contentTags: null,
    corrected: null,
    creditCurationOverride: null,
    credits: null,
    curatedAt: null,
    disableAds: null,
    disclaimer: null,
    documents: null,
    docXml: null,
    embedUrl: null,
    extraAttachments: null,
    extraTags: null,
    facebook: null,
    featureVersion: null,
    footer: null,
    footnotes: null,
    franchise: null,
    header: null,
    headline: null,
    hedAndDekPosition: null,
    id: null,
    iframes: null,
    images: null,
    indexes: null,
    issueId: null,
    isTrending: null,
    keypointsToggle: null,
    label: null,
    labelDropdown: null,
    language: null,
    lede: null,
    ledeKind: null,
    livestream: null,
    magazine: null,
    magazineIssue: null,
    meta: null,
    metadata: null,
    minorUpdatedAt: null,
    mobileLede: null,
    moved: null,
    newsletter: null,
    nicodes: null,
    originalCredits: null,
    overrides: null,
    people: null,
    pillar: null,
    premium: null,
    primaryCategory: null,
    publishedAt: null,
    reaction: null,
    readings: null,
    related: null,
    relatedTitle: null,
    resourceType: null,
    revision: null,
    secondaryBrands: null,
    shortUrl: null,
    show: null,
    site: null,
    sites: null,
    slug: null,
    snapshotDuration: null,
    snapshotEndDate: null,
    snapshotStartDate: null,
    snapshotTicker: null,
    snapshotTitle: null,
    storythreads: null,
    subheadline: null,
    summary: null,
    tags: null,
    template: null,
    terminalTickers: null,
    textLabel: null,
    thumbnail: null,
    tickers: null,
    timeline: null,
    topic: null,
    trashline: null,
    twitter: null,
    type: null,
    updatedAt: null,
    url: null,
    variation: null,
    videoCurationOverride: null,
    videos: null,
    webOriginal: null,
    widget: null,
    wire: null,
    wordCount: null,
    __typename: "Story",
    aiSummary: null,
};

export const DEFAULT_HYDRATED_STORY: CuratedStory = {
    headline: "",
    id: "",
    trackingType: TRACKING_TYPE.Story,
    type: "",
    url: "",
    __modelType: "Story",
};

export const DEFAULT_HYDRATED_RELATED_STORY: RelatedItem = {
    headline: "",
    id: "",
    trackingType: TRACKING_TYPE.Story,
    type: "",
    url: "",
    __modelType: "Story",
};

export const DEFAULT_DAPER_STORY: DaperStory = {
    type: DaperStoryType.newsletter_campaign,
    id: "",
    revision: "",
    wire: {
        mnemonic: "",
        class: 0,
        id: 0,
    },
    updatedAt: "2025-02-02T18:00:34.678Z",
    publishedAt: "2025-02-02T18:00:34.678Z",
    storyUpdatedAt: "2025-02-02T18:00:34.678Z",
    storyPublishedAt: "2025-02-02T18:00:34.678Z",
    groupPublishedAt: "2025-02-02T18:00:34.678Z",
    storyEmbargoEndsAt: "2025-02-02T18:00:34.678Z",
    byline: "",
    summary: {
        text: "",
        type: "text/html",
    },
    autoSummaries: [],
    body: {},
    slug: "",
    url: "",
    primaryCategory: "",
    categories: [],
    chinaExclude: false,
    webOriginal: true,
    authoredRegion: "Global",
    language: "ENGLISH",
    webHidden: false,
    metadata: {},
    readings: [],
    wordCount: 1000,
    resourceType: "Newsletter",
    premium: false,
    editorialTaxonomies: [
        {
            id: "",
            type: "brand",
            role: "primary",
        },
    ],
    russiaExclude: false,
    terminalExclude: false,
    businessFlags: {
        doNotRecirc: false,
        registrationWall: false,
    },
    embeds: {},
    headlines: {
        web: "",
        story: "",
        social: "",
        markup: {
            web: "",
        },
        plain: "",
    },
    attachments: {
        image: {},
        video: {},
        desktop: {},
        videoAudio: {},
        all: [],
    },
    extraMedia: {},
    tags: {},
    contentTags: [],
    credits: {
        by: [
            {
                id: "",
                roles: [],
                _links: {
                    self: {
                        href: "",
                    },
                    details: {
                        href: "",
                    },
                },
            },
        ],
        author: [
            {
                id: "",
                roles: [],
                _links: {
                    self: {
                        href: "",
                    },
                    details: {
                        href: "",
                    },
                },
            },
        ],
        editor: [
            {
                id: "",
                roles: [""],
                _links: {
                    self: {
                        href: "",
                    },
                    details: {
                        href: "",
                    },
                },
            },
        ],
    },
    _links: {
        self: {
            href: "",
        },
    },
    relatedStories: {},
    primaryCategoryData: {
        id: "",
        _links: {
            self: {
                href: "",
            },
        },
    },
    primarySite: DaperStorySite.bbiz,
    sites: [],
    franchise: "",
    label: "",
};

export const mockHydratedStory = (
    args: {
        [K in keyof CuratedStory]?: CuratedStory[K];
    } & { trackingType: TRACKING_TYPE; type: string }
): CuratedStory => {
    return {
        ...DEFAULT_HYDRATED_STORY,
        headline: args.headline ?? "",
        id: args.id ?? "",
        url: args.url ?? "",
        ...args,
    };
};

export const mockWardenclyffeStory = (
    overrides: DeepPartial<Story> = {}
): Story => {
    return new Proxy(deepWrap(overrides) as object, mockHandler) as Story;
};

/** Deep-merge override */
export function mockWardenclyffeStoryDeepMerge(
    overrides: DeepPartial<Story> = {}
): Story {
    const merged = deepMerge(DEFAULT_WARDENCLYFFE_STORY, overrides);

    merged.__typename = "Story";

    return merged as Story;
}

export const mockHydratedItemWithSnapshot = ({
    index,
}: {
    index?: number;
}): CuratedItem => {
    return {
        headline: `Test Headline ${index || 1}`,
        id: `item-id-${index || 1}`,
        media: {
            snapshotChart: {
                snapshotDuration: "ONE_DAY",
                snapshotEndDate: "",
                snapshotTicker: "TSLA:US",
                snapshotStartDate: "",
                snapshotTitle: `Test Snapshot Title ${index || 1}`,
            },
        },
        type: CONTENT_TYPE.article,
        url: `www.bloomberg.com/test-url-${index || 1}`,
        trackingType: TRACKING_TYPE.Story,
    };
};

export const mockHydratedRelatedStory = (args: {
    audios?: Audio[];
    audioImage?: Image;
    brand?: string;
    byline?: string;
    embedUrl?: string;
    eyebrow?: Eyebrow;
    franchise?: string;
    headline: string;
    id: string;
    image?: Image;
    labelDropdown?: string;
    livestream?: string;
    media?: HydratedMediaSourceData;
    meta?: Record<string, unknown>;
    newsletter?: Newsletter;
    nicodes?: string[];
    publishedAt?: string;
    slug?: string;
    textLabel?: string;
    trackingType: TRACKING_TYPE;
    type: string;
    updatedAt?: string;
    url: string;
    variation?: CuratedItemVariation;
    videos?: Video[];
}) => {
    return {
        ...DEFAULT_HYDRATED_RELATED_STORY,
        ...args,
    };
};

export const mockHydratedEyebrow = (args: {
    bybrowEligible?: boolean;
    text: string;
    url: string;
}): Eyebrow => {
    return {
        ...DEFAULT_HYDRATED_EYEBROW,
        ...args,
    };
};

export const mockHydratedRelatedStory1 = () => {
    return mockHydratedRelatedStory({
        byline: "Mr. Byline",
        brand: "opinion",
        headline: "Test Headline",
        id: "relatedId1",
        publishedAt: "2022-04-13T20:50:44.618Z",
        trackingType: TRACKING_TYPE.Interactive,
        type: "article",
        updatedAt: "2022-04-13T20:50:44.618Z",
        url: "https://origin.staging.bloomberg.com/news/articles/2022-03-20/colombia-poll-shows-election-turning-into-two-horse-race-semana1",
        eyebrow: {
            text: "Test Related Eyebrow",
            url: "/test-related-url",
            bybrowEligible: true,
        },
    });
};

export const mockHydratedRelatedStory2 = () => {
    return mockHydratedRelatedStory({
        byline: "Mr. Byline",
        brand: "green",
        headline: "Test Headline",
        id: "relatedId2",
        publishedAt: "2022-04-13T20:50:44.619Z",
        trackingType: TRACKING_TYPE.Interactive,
        type: "liveblog",
        updatedAt: "2022-04-13T20:50:44.619Z",
        url: "https://origin.staging.bloomberg.com/news/articles/2022-03-20/colombia-poll-shows-election-turning-into-two-horse-race-semana2",
        eyebrow: {
            text: "Test Related Eyebrow",
            url: "/test-related-url",
            bybrowEligible: true,
        },
    });
};

export const mockHydratedRelatedStory3 = () => {
    return mockHydratedRelatedStory({
        byline: "Mr. Byline",
        brand: "opinion",
        headline: "Test Headline",
        id: "relatedId3",
        publishedAt: "2022-04-13T20:50:44.608Z",
        trackingType: TRACKING_TYPE.Interactive,
        type: "video",
        updatedAt: "2022-04-13T20:50:44.608Z",
        url: "https://origin.staging.bloomberg.com/news/articles/2022-03-20/colombia-poll-shows-election-turning-into-two-horse-race-semana3",
        eyebrow: {
            text: "Test Related Eyebrow",
            url: "/test-related-url",
            bybrowEligible: true,
        },
    });
};

export const mockHydratedRelatedStory4 = () => {
    return mockHydratedRelatedStory({
        byline: "Mr. Byline",
        brand: "opinion",
        headline: "Test Headline",
        id: "relatedId4",
        publishedAt: "2022-04-13T20:50:44.518Z",
        trackingType: TRACKING_TYPE.Interactive,
        type: "feature",
        updatedAt: "2022-04-13T20:50:44.518Z",
        url: "https://origin.staging.bloomberg.com/news/articles/2022-03-20/colombia-poll-shows-election-turning-into-two-horse-race-semana4",
        eyebrow: {
            text: "Test Related Eyebrow",
            url: "/test-related-url",
            bybrowEligible: true,
        },
    });
};

export const mockHydratedRelatedOpinionStory = () => {
    return mockHydratedRelatedStory({
        byline: "Mr. Byline",
        brand: "opinion",
        headline: "Test Headline",
        id: "relatedId1",
        publishedAt: "2022-04-13T20:50:44.618Z",
        trackingType: TRACKING_TYPE.Interactive,
        type: "article",
        updatedAt: "2022-04-13T20:50:44.618Z",
        url: "https://origin.staging.bloomberg.com/news/articles/2022-03-20/colombia-poll-shows-election-turning-into-two-horse-race-semana1",
        eyebrow: {
            text: "Test Related Eyebrow",
            url: "/test-related-url",
            bybrowEligible: true,
        },
    });
};

export const mockHydratedRelatedStoryWithImage = () => {
    return mockHydratedRelatedStory({
        byline: "Mr. Byline",
        brand: "",
        headline: "Test Headline",
        id: "relatedId1",
        publishedAt: "2022-04-13T20:50:44.618Z",
        trackingType: TRACKING_TYPE.Interactive,
        type: "article",
        updatedAt: "2022-04-13T20:50:44.618Z",
        url: "https://origin.staging.bloomberg.com/news/articles/2022-03-20/colombia-poll-shows-election-turning-into-two-horse-race-semana1",
        eyebrow: {
            text: "Test Related Eyebrow",
            url: "/test-related-url",
            bybrowEligible: true,
        },
        media: MOCK_HYDRATED_MEDIA,
        image: {
            baseUrl:
                "https://assets.bwbx.io/images/users/iqjWHBFdfxIU/ioNuiUz_rB4E/v1/1000x-1.jpg",
            alt: '"Chris Paul" in quotes has broken the site.',
        },
    });
};

export const mockHydratedRelatedStories1 = (): RelatedItem[] => {
    return [
        mockHydratedRelatedStory1(),
        mockHydratedRelatedStory2(),
        mockHydratedRelatedStory3(),
        mockHydratedRelatedStory4(),
    ];
};

export const mockHydratedStory1 = (): CuratedStory => {
    return mockHydratedStory({
        brand: "businessweek",
        byline: "John Beck",
        headline:
            "Ukraine’s Repair Crews Dodge Bullets and Splice Cable to Keep the Country Online",
        id: "RLH79VDWRGG101",
        label: "Feature",
        lede: {
            alt: "Ukrtelecom’s Tatiana Bespalova with the crew she dispatched to Pushcha-Vodytsya.",
            id: "393546439",
            credit: "Photographer: John Beck for Bloomberg Businessweek",
            caption:
                "<p>Ukrtelecom’s Tatiana Bespalova with the crew she dispatched to Pushcha-Vodytsya.</p>",
            title: "FEATURE_UKRTELECOM_01-CMS This image can only be used with attached article for period of 90 days from publication",
            url: "https://assets.bwbx.io/images/users/iqjWHBFdfxIU/ixGZl7PYzFjM/v1/-1x-1.jpg",
        },
        publishedAt: "2022-11-17T05:01:07.598Z",
        relatedStories: mockHydratedRelatedStories1(),
        slug: "2022-11-17/ukraine-stays-online-during-war-thanks-to-repair-crews",
        summary:
            "In the trenches and beneath the utility poles with the teams keeping citizens and soldiers connected",
        thumbnail: {
            id: "393546439",
            type: "image",
            title: "FEATURE_UKRTELECOM_01-CMS This image can only be used with attached article for period of 90 days from publication",
            url: "https://assets.bwbx.io/images/users/iqjWHBFdfxIU/ixGZl7PYzFjM/v1/-1x-1.jpg",
        },
        trackingType: TRACKING_TYPE.Story,
        type: "feature",
        updatedAt: "2022-11-17T05:01:07.601Z",
        url: "/news/features/2022-11-17/ukraine-stays-online-during-war-thanks-to-repair-crews",
    });
};

export const mockWardenclyffeStory1 = () => {
    return mockWardenclyffeStoryDeepMerge({
        brand: "businessweek",
        byline: "John Beck",
        headline: {
            text: "Ukraine’s Repair Crews Dodge Bullets and Splice Cable to Keep the Country Online",
        },
        id: "RLH79VDWRGG101",
        label: "Feature",
        lede: {
            alt: "Ukrtelecom’s Tatiana Bespalova with the crew she dispatched to Pushcha-Vodytsya.",
            id: "393546439",
            credit: "Photographer: John Beck for Bloomberg Businessweek",
            caption:
                "<p>Ukrtelecom’s Tatiana Bespalova with the crew she dispatched to Pushcha-Vodytsya.</p>",
            title: "FEATURE_UKRTELECOM_01-CMS This image can only be used with attached article for period of 90 days from publication",
            url: "https://assets.bwbx.io/images/users/iqjWHBFdfxIU/ixGZl7PYzFjM/v1/-1x-1.jpg",
        },
        publishedAt: "2022-11-17T05:01:07.598Z",
        slug: "2022-11-17/ukraine-stays-online-during-war-thanks-to-repair-crews",
        summary: {
            text: "In the trenches and beneath the utility poles with the teams keeping citizens and soldiers connected.",
            html: "<p>In the trenches and beneath the utility poles with the teams keeping citizens and soldiers connected.</p>",
        },
        thumbnail: {
            id: "393546439",
            type: "image",
            title: "FEATURE_UKRTELECOM_01-CMS This image can only be used with attached article for period of 90 days from publication",
            url: "https://assets.bwbx.io/images/users/iqjWHBFdfxIU/ixGZl7PYzFjM/v1/-1x-1.jpg",
        },
        type: "feature",
        updatedAt: "2022-11-17T05:01:07.601Z",
        url: "https://www.bloomberg.com/news/features/2022-11-17/ukraine-stays-online-during-war-thanks-to-repair-crews",
    });
};

export const mockHydratedStory2 = () => {
    return mockHydratedStory({
        brand: "businessweek",
        byline: "Alex Barinka",
        headline:
            "Meta Confronts an Apple-Sized Hole in Its Once-Mighty Advertising Business",
        id: "RLHL4EDWRGG401",
        label: "Technology",
        lede: {
            id: "393553676",
            credit: "Illustration: Oscar Bolton Green for Bloomberg Businessweek",
            title: "4922A_TECH_META_CMS_01 This image can only be used with attached article for period of 90 days from publication",
            url: "https://assets.bwbx.io/images/users/iqjWHBFdfxIU/i5DlvF7z4Xrg/v0/-1x-1.jpg",
            size: "small",
        },
        publishedAt: "2022-11-17T10:00:14.796Z",
        slug: "2022-11-17/iphone-data-changes-are-biggest-cause-of-meta-meta-woes",
        summary:
            "The social media company must deal with a range of headwinds, but a year later, the hit to its ad targeting capabilities has been particularly painful.",
        thumbnail: {
            id: "393553676",
            type: "image",
            title: "4922A_TECH_META_CMS_01 This image can only be used with attached article for period of 90 days from publication",
            url: "https://assets.bwbx.io/images/users/iqjWHBFdfxIU/ixGZl7PYzFjM/v1/-1x-1.jpg",
        },
        trackingType: TRACKING_TYPE.Story,
        type: "article",
        updatedAt: "2022-11-17T10:00:14.796Z",
        url: "/news/articles/2022-11-17/iphone-data-changes-are-biggest-cause-of-meta-meta-woes",
    });
};

export const mockWardenclyffeStory2 = () => {
    return mockWardenclyffeStoryDeepMerge({
        brand: "businessweek",
        byline: "Alex Barinka",
        headline: {
            text: "Meta Confronts an Apple-Sized Hole in Its Once-Mighty Advertising Business",
        },
        id: "RLHL4EDWRGG401",
        label: "Technology",
        lede: {
            id: "393553676",
            credit: "Illustration: Oscar Bolton Green for Bloomberg Businessweek",
            title: "4922A_TECH_META_CMS_01 This image can only be used with attached article for period of 90 days from publication",
            url: "https://assets.bwbx.io/images/users/iqjWHBFdfxIU/i5DlvF7z4Xrg/v0/-1x-1.jpg",
            size: "small",
        },
        publishedAt: "2022-11-17T10:00:14.796Z",
        slug: "2022-11-17/iphone-data-changes-are-biggest-cause-of-meta-meta-woes",
        summary: {
            text: "The social media company must deal with a range of headwinds, but a year later, the hit to its ad targeting capabilities has been particularly painful.",
            html: "<p>The social media company must deal with a range of headwinds, but a year later, the hit to its ad targeting capabilities has been particularly painful.</p>",
        },
        thumbnail: {
            id: "393553676",
            title: "4922A_TECH_META_CMS_01 This image can only be used with attached article for period of 90 days from publication",
            type: "image",
            url: "https://assets.bwbx.io/images/users/iqjWHBFdfxIU/i5DlvF7z4Xrg/v0/-1x-1.jpg",
        },
        type: "article",
        updatedAt: "2022-11-17T10:00:14.796Z",
        url: "https://www.bloomberg.com/news/articles/2022-11-17/iphone-data-changes-are-biggest-cause-of-meta-meta-woes",
    });
};

export const mockHydratedStory3 = (): CuratedStory => {
    return mockHydratedStory({
        brand: "businessweek",
        byline: "Alan Levin",
        franchise: "the-big-take",
        headline:
            "Fatal Crashes Highlight Rising Danger of Illicit Charter Flights",
        id: "RLGTC7T1UM0W01",
        label: "The Big Take",
        lede: {
            id: "393496858",
            credit: "Photo illustration: 731; Photo: Getty Images (1)",
            type: "image",
            title: "4922_BIZ_CHARTER_01-CMS This image can only be used with attached article for period of 90 days from publication",
            url: "https://assets.bwbx.io/images/users/iqjWHBFdfxIU/iPPdj2oZ0J8A/v0/-1x-1.jpg",
        },
        publishedAt: "2022-11-17T00:00:07.147Z",
        slug: "2022-11-17/cheap-flights-made-charter-plane-business-boom-but-crashes-spark-faa-action",
        summary:
            "Low-cost operators can put flyers in harm’s way, at a time when airlines have never been safer. Regulators are racing to curb the risks.",
        thumbnail: {
            id: "393496858",
            title: "4922_BIZ_CHARTER_01-CMS This image can only be used with attached article for period of 90 days from publication",
            type: "image",
            url: "https://assets.bwbx.io/images/users/iqjWHBFdfxIU/iPPdj2oZ0J8A/v0/-1x-1.jpg",
        },
        trackingType: TRACKING_TYPE.Story,
        type: "feature",
        updatedAt: "2022-11-17T00:00:07.146Z",
        url: "/news/features/2022-11-17/cheap-flights-made-charter-plane-business-boom-but-crashes-spark-faa-action",
    });
};

export const mockWardenclyffeStory3 = () => {
    return mockWardenclyffeStoryDeepMerge({
        brand: "businessweek",
        byline: "Alan Levin",
        franchise: "the-big-take",
        headline: {
            text: "Fatal Crashes Highlight Rising Danger of Illicit Charter Flights",
        },
        id: "RLGTC7T1UM0W01",
        label: "The Big Take",
        lede: {
            id: "393496858",
            credit: "Photo illustration: 731; Photo: Getty Images (1)",
            type: "image",
            title: "4922_BIZ_CHARTER_01-CMS This image can only be used with attached article for period of 90 days from publication",
            url: "https://assets.bwbx.io/images/users/iqjWHBFdfxIU/iPPdj2oZ0J8A/v0/-1x-1.jpg",
        },
        publishedAt: "2022-11-17T00:00:07.147Z",
        slug: "2022-11-17/cheap-flights-made-charter-plane-business-boom-but-crashes-spark-faa-action",
        summary: {
            text: "Low-cost operators can put flyers in harm’s way, at a time when airlines have never been safer. Regulators are racing to curb the risks.",
            html: "<p>Low-cost operators can put flyers in harm’s way, at a time when airlines&nbsp;have never been safer. Regulators are racing to curb the risks.</p>",
        },
        thumbnail: {
            id: "393496858",
            title: "4922_BIZ_CHARTER_01-CMS This image can only be used with attached article for period of 90 days from publication",
            url: "https://assets.bwbx.io/images/users/iqjWHBFdfxIU/iPPdj2oZ0J8A/v0/-1x-1.jpg",
        },
        type: "feature",
        updatedAt: "2022-11-17T00:00:07.146Z",
        url: "https://www.bloomberg.com/news/features/2022-11-17/cheap-flights-made-charter-plane-business-boom-but-crashes-spark-faa-action",
    });
};

export const mockHydratedStoryWithTimestamps = ({
    publishedAt,
    updatedAt,
}: {
    [K in keyof CuratedStory]?: CuratedStory[K];
} = {}) => {
    return mockHydratedStory({
        id: "test-id",
        headline: "Test Headline",
        publishedAt,
        type: CONTENT_TYPE.article,
        trackingType: TRACKING_TYPE.Story,
        updatedAt,
    });
};

export const mockHydratedStoryWithNoId = () => {
    return mockHydratedStory({
        byline: "Ms. Byline",
        headline: "Test Headline",
        type: CONTENT_TYPE.article,
        brand: "opinion",
        trackingType: TRACKING_TYPE.Story,
        url: "www.bloomberg.com/news/articles/slug-1",
    });
};

export const mockHydratedStoryWithNoUrl = () => {
    return mockHydratedStory({
        byline: "Ms. Byline",
        id: "test-id",
        headline: "Test Headline",
        type: CONTENT_TYPE.article,
        brand: "opinion",
        trackingType: TRACKING_TYPE.Story,
    });
};

export const mockHydratedStoryWithRequiredFields = (
    args: {
        [K in keyof CuratedStory]?: CuratedStory[K];
    } & { index?: number } = {}
) => {
    return mockHydratedStory({
        id: "test-id",
        headline: `Test Headline ${args.index || 1}`,
        type: CONTENT_TYPE.article,
        trackingType: TRACKING_TYPE.Story,
        url: "www.bloomberg.com/news/articles/slug-1",
        media: {
            defaultImage: MOCK_HYDRATED_MEDIA.defaultImage,
        },
        ...args,
    });
};

export const mockHydratedStoryWithAllMedia = (
    args: {
        [K in keyof CuratedStory]?: CuratedStory[K];
    } & { index?: number } = {}
) => {
    return mockHydratedStory({
        id: "test-id",
        headline: `Test Headline ${args.index || 1}`,
        type: CONTENT_TYPE.article,
        trackingType: TRACKING_TYPE.Story,
        url: "www.bloomberg.com/news/articles/slug-1",
        media: MOCK_HYDRATED_MEDIA,
        ...args,
    });
};

export const mockHydratedStoryWithNoEyebrow = (
    args: {
        [K in keyof CuratedStory]?: CuratedStory[K];
    } = {}
) => {
    return mockHydratedStory({
        brand: "opinion",
        byline: "Ms. Byline",
        headline: "Test Headline",
        id: "test-id",
        image: mockHydratedImage(),
        summary: "Test Summary",
        trackingType: TRACKING_TYPE.Story,
        type: CONTENT_TYPE.article,
        url: "www.bloomberg.com/news/articles/slug-1",
        ...args,
    });
};

export const mockHydratedStoryWithNoByline = (
    args: {
        [K in keyof CuratedStory]?: CuratedStory[K];
    } = {}
): CuratedStory => {
    return mockHydratedStory({
        eyebrow: mockHydratedEyebrow({
            text: "Test Eyebrow Text",
            url: "/test-eyebrow-url",
        }),
        id: "test-id",
        headline: "Test Headline",
        image: mockHydratedImage(),
        brand: "opinion",
        summary: "Test Summary",
        url: "www.bloomberg.com/news/articles/slug-1",
        trackingType: TRACKING_TYPE.Story,
        type: args.type || CONTENT_TYPE.article,
        ...args,
    });
};

export const mockHydratedStoryWithNoImage = (
    args: {
        [K in keyof CuratedStory]?: CuratedStory[K];
    } = {}
): CuratedStory => {
    return mockHydratedStory({
        eyebrow: mockHydratedEyebrow({
            text: "Test Eyebrow Text",
            url: "/test-eyebrow-url",
        }),
        id: "test-id",
        headline: "Test Headline",
        brand: "test-brand",
        summary: "Test Summary",
        url: "www.bloomberg.com/news/articles/slug-1",
        trackingType: TRACKING_TYPE.Story,
        media: undefined,
        type: args.type || CONTENT_TYPE.article,
        ...args,
    });
};

export const mockHydratedStoryWithNoSummary = (
    args: {
        [K in keyof CuratedStory]?: CuratedStory[K];
    } = {}
): CuratedStory => {
    return mockHydratedStory({
        eyebrow: mockHydratedEyebrow({
            text: "Test Eyebrow Text",
            url: "/test-eyebrow-url",
        }),
        id: "test-id",
        headline: "Test Headline",
        image: mockHydratedImage(),
        brand: "opinion",
        url: "www.bloomberg.com/news/articles/slug-1",
        trackingType: TRACKING_TYPE.Story,
        type: args.type || CONTENT_TYPE.article,
        ...args,
    });
};

export const mockHydratedStoryWithRelatedStories = (
    args: {
        [K in keyof CuratedStory]?: CuratedStory[K];
    } & { index?: number } = {}
) => {
    const { index } = args;

    return mockHydratedStory({
        byline: args.byline || `Author Name ${index || 1}`,
        id: `test-id-${index || 1}`,
        headline: `Test Headline ${index || 1}`,
        url: `www.bloomberg.com/news/articles/slug-${index || 1}`,
        trackingType: TRACKING_TYPE.Story,
        type: args.type || CONTENT_TYPE.article,
        ...args,
    });
};

export const mockHydratedStoryWithExtraAttachments = (
    args: {
        [K in keyof CuratedStory]?: CuratedStory[K];
    } & { index?: number } = {}
) => {
    const { index } = args;

    return mockHydratedStory({
        byline: args.byline || `Author Name ${index || 1}`,
        id: `test-id-${index || 1}`,
        headline: `Test Headline ${index || 1}`,
        url: `www.bloomberg.com/news/articles/slug-${index || 1}`,
        trackingType: TRACKING_TYPE.Story,
        type: args.type || CONTENT_TYPE.article,
        media: {
            carousel: MOCK_HYDRATED_MEDIA.carousel,
        },
        extraAttachments: [
            mockHydratedImage({ type: "image" }),
            mockHydratedVideo({ title: "Test Video", type: "Video" }),
            mockHydratedVideo({
                title: "Test Video",
                type: "Video",
                keywords: [LOOPING_VIDEO_KEYWORD],
                url: "bloomberg.com",
            }),
            mockHydratedImage({ type: "image" }),
        ],
        ...args,
    });
};

export const mockHydratedStoryWithImageLede = (
    args: {
        [K in keyof CuratedStory]?: CuratedStory[K];
    } & { index?: number } = {}
) => {
    const { index } = args;

    return mockHydratedStory({
        byline: `Author Name ${index || 1}`,
        id: `test-id-${index || 1}`,
        image: mockHydratedImage(),
        headline: `Test Headline ${index || 1}`,
        lede: mockHydratedImage(),
        media: {
            defaultImage: mockHydratedImage(),
        },
        url: `www.bloomberg.com/news/articles/slug-${index || 1}`,
        trackingType: TRACKING_TYPE.Story,
        type: args.type || CONTENT_TYPE.article,
        ...args,
    });
};

export const mockHydratedStoryWithImage = (
    args: {
        [K in keyof CuratedStory]?: CuratedStory[K];
    } & { index?: number } = {}
) => {
    const { index } = args;

    return mockHydratedStory({
        byline: `Author Name ${index || 1}`,
        eyebrow: mockHydratedEyebrow({
            text: `Test Eyebrow ${index || 1}`,
            url: `/test-eyebrow-${index || 1}`,
        }),
        id: `test-id-${index || 1}`,
        headline: `Test Headline ${index || 1}`,
        media: {
            defaultImage: MOCK_HYDRATED_MEDIA.defaultImage,
        },
        textLabel: `Test Eyebrow ${index || 1}`,
        image: mockHydratedImage(),
        type: args.type || CONTENT_TYPE.article,
        url: `www.bloomberg.com/news/articles/slug-${index || 1}`,
        trackingType: TRACKING_TYPE.Story,
        ...args,
    });
};

export const mockHydratedStoryWithVideoLede = (
    args: {
        [K in keyof CuratedStory]?: CuratedStory[K];
    } & { index?: number } = {}
) => {
    const { index } = args;

    return mockHydratedStory({
        brand: `test-brand-${index || 1}`,
        byline: `Test Author ${index || 1}`,
        eyebrow: mockHydratedEyebrow({
            text: `Test Eyebrow ${index || 1}`,
            url: `/test-eyebrow-${index || 1}`,
        }),
        id: `test-id-${index || 1}`,
        headline: `Test Headline ${index || 1}`,
        lede: mockHydratedVideo(),
        summary: `Test summary ${index || 1}`,
        url: `www.bloomberg.com/news/articles/slug-${index || 1}`,
        trackingType: TRACKING_TYPE.Video,
        type: args.type || CONTENT_TYPE.article,
        video: mockHydratedVideo(),
        media: {
            vodVideo: MOCK_HYDRATED_MEDIA.vodVideo,
            videoImage: MOCK_HYDRATED_MEDIA.videoImage,
        },
        ...args,
    });
};

export const mockHydratedStoryWithAudio = (
    args: {
        [K in keyof CuratedStory]?: CuratedStory[K];
    } & { index?: number } = {}
) => {
    const { index } = args;

    return mockHydratedStory({
        byline: `Test Author Byline ${index || 1}`,
        id: `test-id-${index || 1}`,
        headline: `Test Headline ${index || 1} `,
        url: `www.bloomberg.com/news/audio/slug-${index || 1}`,
        type: CONTENT_TYPE.audio,
        audios: [mockAudio()],
        trackingType: TRACKING_TYPE.Story,
        audioImage: mockHydratedImage(),
        moduleMeta: {
            labelUrl: "test-labelUrl",
            subtitle: "test-subtitle",
            title: "test-title",
        },
        media: {
            audioImage: mockHydratedImage(),
        },
        ...args,
    });
};

export const mockHydratedStoryWithEmbedUrl = (
    args: {
        [K in keyof CuratedStory]?: CuratedStory[K];
    } & { index?: number } = {}
) => {
    const { index } = args;

    return mockHydratedStory({
        byline: `Test Author Byline ${index || 1}`,
        id: `test-id-${index || 1}`,
        headline: `Test Headline ${index || 1} `,
        url: `www.bloomberg.com/news/audio/slug-${index || 1}`,
        embedUrl: `https://www.bloomberg.com/embed-this-url`,
        trackingType: TRACKING_TYPE.Story,
        type: args.type || CONTENT_TYPE.article,
        media: {
            embeddedIframe: `https://www.bloomberg.com/embed-this-url`,
        },
        ...args,
    });
};

export const mockHydratedStoryWithLivestream = (
    args: {
        [K in keyof CuratedStory]?: CuratedStory[K];
    } & { index?: number } = {}
) => {
    const { index } = args;

    return mockHydratedStory({
        byline: `Test Author Byline ${index || 1}`,
        id: `test-id-${index || 1}`,
        headline: `Test Headline ${index || 1} `,
        url: `www.bloomberg.com/news/audio/slug-${index || 1}`,
        embedUrl: `https://www.bloomberg.com/embed-this-url`,
        trackingType: TRACKING_TYPE.Story,
        type: args.type || CONTENT_TYPE.article,
        livestream: "US",
        media: {
            livestreamVideo: "US",
        },
        ...args,
    });
};

export const mockHydratedStoryWithVideo = (
    args: {
        [K in keyof CuratedStory]?: CuratedStory[K];
    } & { index?: number } = {}
) => {
    const { index } = args;

    return mockHydratedStory({
        byline: `Test Author Byline ${index || 1}`,
        id: `test-id-${index || 1}`,
        headline: `Test Headline ${index || 1} `,
        url: `www.bloomberg.com/news/articles/slug-${index || 1}`,
        trackingType: TRACKING_TYPE.Video,
        type: CONTENT_TYPE.video,
        video: mockHydratedVideo(),
        image: mockHydratedImage(),
        media: {
            vodVideo: {
                video: mockHydratedVideo(),
                image: mockHydratedImage(),
            },
            defaultImage: mockHydratedImage(),
            videoImage: {
                image: mockHydratedImage(),
                videoDuration: 821280,
            },
        },
        ...args,
    });
};

export const mockHydratedStoryWithVideoOverride = (
    args: {
        [K in keyof CuratedStory]?: CuratedStory[K];
    } & { index?: number } = {}
) => {
    return mockHydratedStoryWithVideo({
        videoCurationOverride: mockHydratedVideo({}),
        media: {
            vodVideo: {
                video: mockHydratedVideo({ isVideoCurationOverride: true }),
                image: mockHydratedImage(),
            },
        },
        ...args,
    });
};

export const mockHydratedStoryWithLoopingVideo = (
    args: {
        [K in keyof CuratedStory]?: CuratedStory[K];
    } & { index?: number } = {}
) => {
    return mockHydratedStoryWithVideo({
        media: {
            loopingVideo: MOCK_HYDRATED_MEDIA.loopingVideo,
        },
        ...args,
    });
};

export const mockHydratedStoryWithVerticalVideoStory = (
    args: {
        [K in keyof CuratedStory]?: CuratedStory[K];
    } & { index?: number } = {}
) => {
    const { index } = args;

    return mockHydratedStory({
        byline: `Test Author Byline ${index || 1}`,
        id: `test-id-${index || 1}`,
        headline: `Test Headline ${index || 1} `,
        url: `www.bloomberg.com/news/articles/slug-${index || 1}`,
        trackingType: TRACKING_TYPE.Video,
        type: CONTENT_TYPE.video,
        video: mockHydratedVerticalVideo(),
        media: {
            verticalVideo: MOCK_HYDRATED_MEDIA.verticalVideo,
        },
        ...args,
    });
};

export const mockHydratedStoryWithVideoStoryWithImage = (
    args: {
        [K in keyof CuratedStory]?: CuratedStory[K];
    } & { index?: number } = {}
) => {
    const { index } = args;

    return mockHydratedStory({
        byline: `Test Author Byline ${index || 1}`,
        id: `test-id-${index || 1}`,
        headline: `Test Headline ${index || 1} `,
        url: `www.bloomberg.com/news/articles/slug-${index || 1}`,
        trackingType: TRACKING_TYPE.Video,
        type: CONTENT_TYPE.video,
        image: { baseUrl: "test-item-image" },
        video: { ...mockHydratedVideo(), ...{ thumbnail: undefined } },
        media: {
            vodVideo: {
                video: { ...mockHydratedVideo(), ...{ thumbnail: undefined } },
                image: mockHydratedImage(),
            },
        },
        ...args,
    });
};

export const mockHydratedStoryWithVideoStoryWithoutImage = (
    args: {
        [K in keyof CuratedStory]?: CuratedStory[K];
    } & { index?: number } = {}
) => {
    const { index } = args;

    return mockHydratedStory({
        byline: `Test Author Byline ${index || 1}`,
        id: `test-id-${index || 1}`,
        headline: `Test Headline ${index || 1} `,
        url: `www.bloomberg.com/news/articles/slug-${index || 1}`,
        trackingType: TRACKING_TYPE.Video,
        type: CONTENT_TYPE.video,
        image: undefined,
        video: {
            ...mockHydratedVideo(),
            ...{ thumbnail: { baseUrl: "test-video-thumbnail" } },
        },
        media: {
            vodVideo: {
                video: mockHydratedVideo(),
            },
        },
        ...args,
    });
};

export const mockHydratedStoryWithVideoStoryWithoutImageAndThumbnail = (
    args: {
        [K in keyof CuratedStory]?: CuratedStory[K];
    } & { index?: number } = {}
) => {
    const { index } = args;

    return mockHydratedStory({
        byline: `Test Author Byline ${index || 1}`,
        id: `test-id-${index || 1}`,
        headline: `Test Headline ${index || 1} `,
        url: `www.bloomberg.com/news/articles/slug-${index || 1}`,
        trackingType: TRACKING_TYPE.Video,
        type: CONTENT_TYPE.video,
        image: undefined,
        video: {
            ...mockHydratedVideo(),
            ...{ thumbnail: undefined },
        },
        media: {
            vodVideo: {
                video: { ...mockHydratedVideo(), ...{ thumbnail: undefined } },
            },
        },
        ...args,
    });
};

export const mockHydratedStoryWithVerticalVideoCurationOverride = (
    args: {
        [K in keyof CuratedStory]?: CuratedStory[K];
    } & { index?: number } = {}
) => {
    const { index } = args;

    return mockHydratedStory({
        byline: `Test Author Byline ${index || 1}`,
        id: `test-id-${index || 1}`,
        headline: `Test Headline ${index || 1} `,
        url: `www.bloomberg.com/news/articles/slug-${index || 1}`,
        trackingType: TRACKING_TYPE.Video,
        type: CONTENT_TYPE.article,
        videoCurationOverride: mockHydratedVerticalVideo(),
        variation: CuratedItemVariation.SmallVerticalVideoPlayer,
        media: {
            verticalVideo: {
                video: MOCK_HYDRATED_VERTICAL_VIDEO_AS_DECORATION,
                image: MOCK_HYDRATED_MEDIA.verticalVideo.image,
                isVideoCurationOverride: true,
                useSmallPlayer: true,
            },
        },
        ...args,
    });
};

export const mockHydratedStoryWithVerticalVideoStoryAndThumbnailOverride = (
    args: {
        [K in keyof CuratedStory]?: CuratedStory[K];
    } & { index?: number } = {}
) => {
    const { index } = args;

    return mockHydratedStoryWithVerticalVideoStory({
        image: mockHydratedImage({
            baseUrl: `www.bloomberg.com/test-vertical-video-thumbnail-override-${
                index || 1
            }`,
        }),
        media: {
            verticalVideo: {
                video: MOCK_HYDRATED_MEDIA.verticalVideo.video,
                image: mockHydratedImage({
                    baseUrl: `www.bloomberg.com/test-vertical-video-thumbnail-override-${
                        index || 1
                    }`,
                }),
            },
        },
        ...args,
    });
};

export const mockHydratedStoryWithNewsletterCampaign = (args: {
    [K in keyof CuratedStory]?: CuratedStory[K];
}) => {
    return mockHydratedStory({
        brand: "citylab",
        headline: "Test Newsletter Headline",
        id: "test-id",
        image: mockHydratedImage(),
        newsletter: mockHydratedNewsletterCampaign({
            title: "CityLab Daily",
            images: [
                mockHydratedNewsletterImage({
                    baseUrl:
                        "www.bloomberg.com/newsletter-campaign-image-url-1",
                    placement: ["card.default", "card.mobile"],
                }),
                mockHydratedNewsletterImage({
                    baseUrl:
                        "www.bloomberg.com/newsletter-campaign-image-url-2",
                    placement: ["card.standalone"],
                }),
            ],
        }),
        publishedAt: "2023-12-05T18:32:41.685Z",
        slug: "slug-newsletter-campaign",
        trackingType: TRACKING_TYPE.Story,
        type: CONTENT_TYPE.newsletter_campaign,
        updatedAt: "2023-12-05T18:32:41.684Z",
        url: "www.bloomberg.com/news/articles/slug-newsletter-campaign",
        media: {
            newsletterImage: MOCK_HYDRATED_MEDIA.newsletterImage,
        },
        ...args,
    });
};

export const mockHydratedStoryWithMedia = (
    args: {
        [K in keyof CuratedStory]?: CuratedStory[K];
    } = {}
) => {
    return mockHydratedStory({
        id: "test-id",
        headline: "Test Headline",
        url: "www.bloomberg.com/news/articles/slug-1",
        trackingType: TRACKING_TYPE.Video,
        type: args.type || CONTENT_TYPE.article,
        media: MOCK_HYDRATED_MEDIA,
        ...args,
    });
};

export const mockHydratedStoryWithAuthorImage = (
    args: {
        [K in keyof CuratedStory]?: CuratedStory[K];
    } = {}
) => {
    return mockHydratedStory({
        id: "test-id",
        headline: "Test Headline",
        url: "www.bloomberg.com/news/articles/slug-1",
        trackingType: TRACKING_TYPE.Video,
        type: args.type || CONTENT_TYPE.article,
        media: MOCK_HYDRATED_MEDIA,
        ...args,
    });
};

export const mockHydratedStoryWithNoHeadline = (
    args: {
        [K in keyof CuratedStory]?: CuratedStory[K];
    } = {}
) => {
    return mockHydratedStory({
        id: "test-id",
        brand: "test-brand",
        byline: "Test Author 1",
        eyebrow: mockHydratedEyebrow({
            text: "Test Eyebrow",
            url: "/test-eyebrow",
        }),
        url: "www.bloomberg.com/news/articles/slug-1",
        summary: "Test Summary",
        trackingType: TRACKING_TYPE.Story,
        type: args.type || CONTENT_TYPE.article,
        ...args,
    });
};

export const mockHydratedLiveBlogPost = (
    args: {
        [K in keyof LiveBlogPost]?: LiveBlogPost[K];
    } & { index?: number } = {}
): LiveBlogPost => {
    const { index } = args;

    return {
        content: `Test liveblog post content ${index || 1}`,
        id: `test--liveblog-post-id-${index || 1}`,
        updatedAt: "2022-04-13T20:50:44.618Z",
        ...args,
    };
};

export const mockHydratedStoryWithColumnistByline = (
    args: {
        [K in keyof CuratedStory]?: CuratedStory[K];
    } & { index?: number } = {}
) => {
    const { index } = args;

    return mockHydratedStory({
        publishedAt: "2022-04-13T20:50:44.618Z",
        updatedAt: "2022-04-13T20:50:44.618Z",
        summary: `Test Summary ${index || 1}`,
        id: `test-id-${index || 1}`,
        byline: `Ms. Byline ${index || 1}`,
        credits: [mockColumnist({ name: `Ms. Byline ${index || 1}` })],
        eyebrow: mockHydratedEyebrow({
            bybrowEligible: true,
            text: `Test Eyebrow ${index || 1}`,
            url: `/test-eyebrow-${index || 1}`,
        }),
        headline: `Test Headline ${index || 1}`,
        url: `https://www.bloomberg.com/test-article-url-${index || 1}`,
        brand: "opinion",
        trackingType: TRACKING_TYPE.Story,
        image: mockHydratedImage({
            baseUrl: `www.bloomberg.com/test-image-url-${index || 1}`,
        }),
        magazineCoverImage: {
            baseUrl: `assets.bwbx.io/test-magazine-cover-image-url-${
                index || 1
            }`,
            origWidth: 1500,
            origHeight: 2000,
            type: "image",
        },
        magazineIssueDate: "September 26, 2022",
        magazineIssueUrl: `/test-magazine-issue-url-${index || 1}`,
        type: CONTENT_TYPE.article,
        media: {
            defaultImage: MOCK_HYDRATED_MEDIA.defaultImage,
        },
        ...args,
    });
};

export const mockHydratedStoryWithImageEyebrow = () => {
    return mockHydratedStoryWithRequiredFields({
        eyebrow: mockHydratedEyebrow({
            text: "Eyebrow Text",
            url: "www.bloomberg.com/eyebrow-url",
        }),
        image: mockHydratedImage(),
    });
};

export const mockHydratedStoryWithMagazineCoverImage = () => {
    return mockHydratedStoryWithRequiredFields({
        magazineCoverImage: {
            baseUrl: "assets.bwbx.io/test-magazine-cover-image-url-1",
            origWidth: 1500,
            origHeight: 2000,
            type: "image",
        },
        media: {
            magazineCoverImage: MOCK_HYDRATED_MEDIA.magazineCoverImage,
        },
        magazineIssueDate: "September 26, 2022",
        magazineIssueUrl: "/test-magazine-issue-url-1",
    });
};

export const mockHydratedItemsWithMultimedia = (items: CuratedItem[] = []) => {
    return [
        mockHydratedStoryWithImage(),
        mockHydratedStoryWithVideo({
            type: CONTENT_TYPE.video,
        }),
        mockHydratedStoryWithLoopingVideo(),
        mockHydratedStoryWithRequiredFields({
            embedUrl: "www.bloomberg.com/embed-url",
            media: {
                embeddedIframe: "www.bloomberg.com/embed-url",
            },
        }),
        mockHydratedItemWithSnapshot({ index: 1 }),
        ...items,
    ];
};

export const mockHydratedStories1 = (): CuratedStory[] => {
    const mockAuthor1 = "Ms. Byline";
    const mockAuthor2 = "Author 2";
    const mockAuthor3 = "Author 3";
    const mockAuthor4 = "Author 4";
    const mockAuthor5 = "Author 5";

    const mockItems = [
        mockHydratedStoryWithColumnistByline({
            byline: mockAuthor1,
            credits: [mockColumnist({ name: mockAuthor1 })],
            headline: "Test Headline",
            id: "test-id-1",
            index: 1,
            relatedStories: mockHydratedRelatedStories1(),
        }),
        mockHydratedStoryWithColumnistByline({
            byline: mockAuthor2,
            credits: [mockColumnist({ name: mockAuthor2 })],
            headline: "Test Headline",
            id: "test-id-2",
            index: 2,
        }),
        mockHydratedStoryWithColumnistByline({
            byline: mockAuthor3,
            credits: [mockColumnist({ name: mockAuthor3 })],
            headline: "Test Headline",
            id: "test-id-3",
            index: 3,
        }),
        mockHydratedStoryWithColumnistByline({
            byline: mockAuthor4,
            credits: [mockColumnist({ name: mockAuthor4 })],
            headline: "Test Headline",
            id: "test-id-4",
            index: 4,
        }),
        mockHydratedStoryWithColumnistByline({
            byline: mockAuthor5,
            credits: [mockColumnist({ name: mockAuthor5 })],
            headline: "Test Headline",
            id: "test-id-5",
            index: 5,
        }),
    ];

    return mockItems;
};

export const mockHydratedStoryMarketMover = (
    args: {
        [K in keyof MarketMover]?: MarketMover[K];
    } & { index?: number }
): MarketMover => {
    return {
        ...DEFAULT_MARKET_MOVER,
        tickerId: `ticker-id-${args.index || 1}`,
        price: 100.0 * (args.index || 0),
        storyHeadline: `Ticker Story Headline ${args.index || 1}`,
        storyUrl: `www.bloomberg.com/ticker-story-url-${args.index || 1}`,
        ...args,
    };
};

export const mockHydratedMarketMoverStories = (): MarketMover[] => {
    return [
        mockHydratedStoryMarketMover({ index: 1, percentChange: 0.6 }),
        mockHydratedStoryMarketMover({ index: 2, percentChange: -0.8 }),
        mockHydratedStoryMarketMover({ index: 3, percentChange: 0.4 }),
        mockHydratedStoryMarketMover({ index: 4, percentChange: 0.9 }),
    ];
};

const MOCK_STORY_HEADLINES = [
    "Five Things to Start Your Day",
    "Biggest Movers",
    "BMW Gobbles Tesla for Thanksgiving",
    "Meta Changes Back to Facebook",
];

export const mockDaperStoryHeadlines = (
    params: {
        [K in keyof DaperStoryHeadlines]?: DaperStoryHeadlines[K];
    } & { index?: number }
): DaperStoryHeadlines => {
    const i = params?.index || 1;

    return {
        web: `Headline For Web ${i}`,
        markup: {
            web: `Headline For Web ${i}`,
        },
        plain: `Plain Headline ${i}`,
        story: `Story Headline ${i}`,
        social: `Social Headline ${i}`,
    };
};

export const mockDaperStoryAuthor = (
    params: {
        [K in keyof DaperStoryHeadlines]?: DaperStoryHeadlines[K];
    } & { index?: number }
): DaperStoryHeadlines => {
    const i = params?.index || 1;

    return {
        web: `Headline For Web ${i}`,
        markup: {
            web: `Headline For Web ${i}`,
        },
        plain: `Plain Headline ${i}`,
        story: `Story Headline ${i}`,
        social: `Social Headline ${i}`,
    };
};

export const mockDaperStory = (
    args: {
        [K in keyof DaperStory]?: DaperStory[K];
    } & { index?: number }
): DaperStory => ({
    ...DEFAULT_DAPER_STORY,
    id: `test-id-${args.index || 1}`,
    headlines: mockDaperStoryHeadlines({ index: args.index }),
    slug: `https://www.bloomberg.com/news/articles/${args.index || 1}`,
    ...args,
});

export const mockDaperStoryWithImage = (
    args: {
        [K in keyof DaperStory]?: DaperStory[K];
    } & { index?: number; imageUrl?: string }
): DaperStoryWithImage => ({
    ...DEFAULT_DAPER_STORY,
    id: `test-id-${args.index || 1}`,
    headlines: mockDaperStoryHeadlines({ index: args.index }),
    slug: `https://www.bloomberg.com/news/articles/${args.index || 1}`,
    image: args.imageUrl
        ? ({
              baseUrl: args.imageUrl,
              images: {},
              id: `id-${args.imageUrl}`,
          } as DaperImages)
        : undefined,
    ...args,
});

export const mockStoriesFromDaper = (): DaperStory[] =>
    MOCK_STORY_HEADLINES.map((h) =>
        mockDaperStory({
            headlines: {
                web: h,
                markup: { web: h },
                plain: h,
                social: h,
                story: h,
            },
        })
    );
